import { Box, Grid, GridItem, VStack, Heading, Text, Button, Image, Tooltip, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure, Input } from '@chakra-ui/react';
import { STREAM_URLS, API_ENDPOINTS, IMAGE_PATHS} from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { useGlobalAccess } from '../../context/GlobalAccessContext';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';

function LandingPage() {
  const { user, logout } = useAuth();
  const { globalAviationAccess } = useGlobalAccess();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isSlackDialogOpen, setSlackDialogOpen] = useState(false);
  const [isZelloDialogOpen, setZelloDialogOpen] = useState(false);
  const [isZelloPasswordDialogOpen, setZelloPasswordDialogOpen] = useState(false);
  const [zelloPassword, setZelloPassword] = useState('');
  const [zelloPasswordError, setZelloPasswordError] = useState(false);
  const [zelloAccessType, setZelloAccessType] = useState<'siyata' | 'admin'>('siyata');

  console.log('Current user:', user); // Debug log
  console.log('Access permissions:', user?.access); // Debug log
  console.log('Global aviation access:', globalAviationAccess); // Debug log

  // Helper function to check if user has aviation access (either individual or global)
  const hasAviationAccess = () => {
    return user?.access?.aviation || globalAviationAccess;
  };

  const handleStreamAccess = async (streamKey: keyof typeof STREAM_URLS) => {
    try {
      const response = await fetch(`/api${API_ENDPOINTS.validateStream}`, {
        headers: {
          'X-Original-URI': STREAM_URLS[streamKey].page,
        },
        credentials: 'include'
      });
  
      if (response.ok) {
        // Navigate to the stream page in the same window
        window.location.href = STREAM_URLS[streamKey].page;
      }
    } catch (error) {
      console.error('Stream access error:', error);
    }
  };
  
  

  const handleInsightClick = () => {
    onOpen();
  };

  const handleInsightConfirm = () => {
    onClose();
    navigate('/insight');
  };

  const handleZelloAccess = () => {
    setZelloDialogOpen(true);
  };

  const handleZelloOptionSelect = (option: 'siyata' | 'admin') => {
    setZelloDialogOpen(false);
    setZelloAccessType(option);
    setZelloPasswordDialogOpen(true);
  };

  const handleZelloPasswordSubmit = async () => {
    const correctPassword = zelloAccessType === 'siyata' ? 'roip' : 'Rtc2025!';
    
    if (zelloPassword === correctPassword) {
      try {
        if (zelloAccessType === 'siyata') {
          const response = await fetch(`/api${API_ENDPOINTS.validateZello}`, {
            credentials: 'include'
          });
      
          if (response.ok) {
            setZelloPasswordDialogOpen(false);
            setZelloPasswordError(false);
            window.location.href = '/dashboard/';
          } else {
            setZelloPasswordError(true);
          }
        } else {
          // For Zello Admin, just navigate to the admin page
          setZelloPasswordDialogOpen(false);
          setZelloPasswordError(false);
          navigate('/zello-admin');
        }
      } catch (error) {
        console.error('Zello access error:', error);
        setZelloPasswordError(true);
      }
    } else {
      setZelloPasswordError(true);
    }
  };

  return (
    <>
      <Box 
        minH="100vh" 
        minW="100vw"
        margin={0}
        padding={0}
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="gray.900"
        overflowY="auto"
      >
        {/* Hero Section */}
        <Box position="relative" h="400px" w="100%">
          <Image
            src={IMAGE_PATHS.header}
            alt="RTC Header"
            position="absolute"
            top={0}
            left={0}
            right={0}
            w="100%"
            h="100%"
            objectFit="cover"
            objectPosition="center"
          />
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="linear-gradient(to bottom, rgba(26, 32, 44, 0.5), rgba(26, 32, 44, 0.95))"
            zIndex={1}
          />
          <VStack
            position="relative"
            zIndex={2}
            h="100%"
            justify="center"
            spacing={6}
            px={4}
          >
            <Heading
              size="2xl"
              color="white"
              textShadow="2px 2px 4px rgba(0,0,0,0.4)"
            >
              Welcome to RTC Resources
            </Heading>
            {user && (
              <Text
                color="white"
                fontSize="xl"
                textShadow="1px 1px 2px rgba(0,0,0,0.4)"
              >
                Logged in as {user.email}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Main Content */}
        <Box
          w="100%"
          bg="gray.900"
          py={12}
          px={4}
        >
          <VStack spacing={8} maxW="1400px" mx="auto">
            <Grid
              templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }}
              gap={4}
              w="100%"
              justifyItems="center"
              py={12}
            >
              <StreamButton
                onClick={() => handleStreamAccess('secure_ops1')}
                label={STREAM_URLS.secure_ops1.label}
                icon="/images/ops1_icon.PNG"
              />
              <StreamButton
                onClick={() => handleStreamAccess('secure_ops2')}
                label="RTC AppleTV Stream"
                icon="/images/appletv_icon.png"
              />
              {hasAviationAccess() && (
                <StreamButton
                  onClick={() => handleStreamAccess('secure_aviation')}
                  label="WCPD Air3 Downlink"
                  icon="/images/aviation_downlink_icon.PNG"
                />
              )}
              {hasAviationAccess() && (
                <StreamButton
                  onClick={() => handleStreamAccess('secure_air2')}
                  label="WCPD Air2 Downlink"
                  icon="/images/aviation_downlink_icon.PNG"
                />
              )}
              {user?.access?.insight && (
                <StreamButton
                  onClick={handleInsightClick}
                  label="InSight Facial Rec"
                  icon="/images/insight_icon.PNG"
                />
              )}
              {/* Merlin - public */}
              <StreamButton
                onClick={() => window.open('https://merlin.westchesterrtc.com/', '_blank')}
                label="Merlin Map"
                icon="/images/merlin_icon.png"
              />
              {/* CopBinder - public */}
              <StreamButton
                onClick={() => window.open('https://copbinder.com/', '_blank')}
                label="CopBinder"
                icon="/images/copbinder_icon.png"
              />
              {user?.access?.insight && (
                <StreamButton
                  onClick={() => navigate('/rtcai-frame')}
                  label="RTC Bulletin AI (BETA)"
                  icon="/images/rtc_bulletin_icon.PNG"
                />
              )}
              {user?.access?.guacamole && (
                <StreamButton
                  onClick={() => window.open(API_ENDPOINTS.guacamole, '_blank')}
                  label="Guacamole Remote Desktop"
                  icon="/images/guacamole_icon.png"
                />
              )}
              <StreamButton
                onClick={() => setSlackDialogOpen(true)}
                label="Slack Archive"
                icon="/images/slack_archive_icon.PNG"
              />
              <StreamButton
                onClick={handleZelloAccess}
                label="Zello Admins"
                icon="/images/zello_icon.png"
                iconSize="150px"
              />
              <StreamButton
                onClick={() => window.open('https://dpslpr.westchestergov.com/', '_blank')}
                label="OpenALPR"
                icon="/images/lpr_icon.png"
              />
              <StreamButton
                onClick={() => window.open('https://licenseplates.cc/', '_blank')}
                label="License Plate Reference"
                icon="/images/plate_reference_icon.png"
              />
              {user?.access?.admin && (
                <StreamButton
                  onClick={() => navigate('/admin')}
                  label="Admin Dashboard"
                  icon="/images/admin_dashboard_icon.png"
                />
              )}
              {user && (
                <StreamButton
                  onClick={logout}
                  label="Logout"
                  icon="/images/logout_icon.png"
                />
              )}
            </Grid>
          </VStack>
        </Box>

        {/* InSight Access - replace the existing InSight StreamButton with this */}
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isCentered
          motionPreset="slideInBottom"
        >
          <AlertDialogOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)">
            <AlertDialogContent 
              bg="gray.800" 
              color="white"
              borderRadius="20px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
              maxW="500px"
              mx={4}
            >
              <AlertDialogHeader 
                fontSize="xl" 
                fontWeight="600"
                textAlign="center"
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                RTC InSight Disclaimer
              </AlertDialogHeader>

              <AlertDialogBody textAlign="center" py={6}>
                <Text mb={4} fontSize="md" color="gray.300">
                  This tool assists in identifying potential suspects but is not always accurate. Results may vary with image quality and conditions and should not be the sole basis for decision-making.
                </Text>
                <Text mb={4} fontSize="md" color="gray.300">
                  Results are intended for investigative leads only and are not definitive identifications. All matches should be verified through additional investigation.
                </Text>
                <Text fontSize="md" color="gray.300">
                  Do not use results as the sole basis for any law enforcement actions. Establish probable cause independently of this tool.
                </Text>
              </AlertDialogBody>

              <AlertDialogFooter justifyContent="center" borderTop="1px solid" borderColor="gray.700" pt={4}>
                <Button 
                  colorScheme="blue" 
                  onClick={handleInsightConfirm}
                  size="lg"
                  px={8}
                  borderRadius="full"
                  bg="blue.500"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                >
                  I Understand
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Slack Archive credentials dialog */}
        <AlertDialog
          isOpen={isSlackDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setSlackDialogOpen(false)}
          isCentered
          motionPreset="slideInBottom"
        >
          <AlertDialogOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)">
            <AlertDialogContent 
              bg="gray.800" 
              color="white"
              borderRadius="20px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
              maxW="500px"
              mx={4}
            >
              <AlertDialogHeader 
                fontSize="xl" 
                fontWeight="600"
                textAlign="center"
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                Slack Archive Login
              </AlertDialogHeader>
              <AlertDialogBody textAlign="center" py={6}>
                <Text mb={2} fontSize="md" color="gray.300">
                  Username: <b>rtcarchive</b>
                </Text>
                <Text mb={4} fontSize="md" color="gray.300">
                  Password: <b>rtcarchive</b>
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Click OK to open Slack Archive in a new tab.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter justifyContent="center" borderTop="1px solid" borderColor="gray.700" pt={4}>
                <Button 
                  ref={cancelRef} 
                  onClick={() => setSlackDialogOpen(false)} 
                  mr={3}
                  size="lg"
                  px={8}
                  borderRadius="full"
                  variant="ghost"
                  color="gray.300"
                  _hover={{ bg: 'gray.700' }}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={() => {
                    setSlackDialogOpen(false);
                    window.open('/mattermost', '_blank');
                  }}
                  size="lg"
                  px={8}
                  borderRadius="full"
                  bg="blue.500"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                >
                  Open Slack Archive
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Zello Access Type Selection Dialog */}
        <AlertDialog
          isOpen={isZelloDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setZelloDialogOpen(false)}
          isCentered
          motionPreset="slideInBottom"
        >
          <AlertDialogOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)">
            <AlertDialogContent 
              bg="gray.800" 
              color="white"
              borderRadius="20px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
              maxW="500px"
              mx={4}
            >
              <AlertDialogHeader 
                fontSize="xl" 
                fontWeight="600"
                textAlign="center"
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                Zello Access Type
              </AlertDialogHeader>
              <AlertDialogBody textAlign="center" py={6}>
                <Text mb={4} fontSize="md" color="gray.300">
                  Please select your Zello access type:
                </Text>
                <VStack spacing={3}>
                  <Button
                    onClick={() => handleZelloOptionSelect('siyata')}
                    size="lg"
                    w="100%"
                    bg="blue.500"
                    _hover={{ bg: 'blue.600' }}
                    _active={{ bg: 'blue.700' }}
                  >
                    Siyata (ROIP)
                  </Button>
                  <Button
                    onClick={() => handleZelloOptionSelect('admin')}
                    size="lg"
                    w="100%"
                    bg="green.500"
                    _hover={{ bg: 'green.600' }}
                    _active={{ bg: 'green.700' }}
                  >
                    Zello Admin
                  </Button>
                </VStack>
              </AlertDialogBody>
              <AlertDialogFooter justifyContent="center" borderTop="1px solid" borderColor="gray.700" pt={4}>
                <Button 
                  ref={cancelRef} 
                  onClick={() => setZelloDialogOpen(false)} 
                  size="lg"
                  px={8}
                  borderRadius="full"
                  variant="ghost"
                  color="gray.300"
                  _hover={{ bg: 'gray.700' }}
                >
                  Cancel
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Zello Password Dialog */}
        <AlertDialog
          isOpen={isZelloPasswordDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setZelloPasswordDialogOpen(false)}
          isCentered
          motionPreset="slideInBottom"
        >
          <AlertDialogOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)">
            <AlertDialogContent 
              bg="gray.800" 
              color="white"
              borderRadius="20px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.2)"
              maxW="500px"
              mx={4}
            >
              <AlertDialogHeader 
                fontSize="xl" 
                fontWeight="600"
                textAlign="center"
                pb={2}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                Enter Password
              </AlertDialogHeader>
              <AlertDialogBody textAlign="center" py={6}>
                <Text mb={4} fontSize="md" color="gray.300">
                  Please enter the password for {zelloAccessType === 'siyata' ? 'Siyata (ROIP)' : 'Zello Admin'} access:
                </Text>
                <Input
                  type="password"
                  value={zelloPassword}
                  onChange={(e) => setZelloPassword(e.target.value)}
                  placeholder="Enter password"
                  bg="gray.700"
                  border="1px solid"
                  borderColor={zelloPasswordError ? "red.500" : "gray.600"}
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleZelloPasswordSubmit();
                    }
                  }}
                />
                {zelloPasswordError && (
                  <Text color="red.400" fontSize="sm" mt={2}>
                    Incorrect password. Please try again.
                  </Text>
                )}
              </AlertDialogBody>
              <AlertDialogFooter justifyContent="center" borderTop="1px solid" borderColor="gray.700" pt={4}>
                <Button 
                  ref={cancelRef} 
                  onClick={() => {
                    setZelloPasswordDialogOpen(false);
                    setZelloPassword('');
                    setZelloPasswordError(false);
                  }} 
                  mr={3}
                  size="lg"
                  px={8}
                  borderRadius="full"
                  variant="ghost"
                  color="gray.300"
                  _hover={{ bg: 'gray.700' }}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleZelloPasswordSubmit}
                  size="lg"
                  px={8}
                  borderRadius="full"
                  bg="blue.500"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                >
                  Submit
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </>
  );
}

type StreamButtonProps = {
  onClick: () => void;
  label: string;
  icon: string;
  iconSize?: string;
};

const StreamButton = ({ onClick, label, icon, iconSize = "180px" }: StreamButtonProps) => (
  <Button
    onClick={onClick}
    w="180px"
    h="180px"
    bg="transparent"
    border="none"
    boxShadow="none"
    borderRadius="xl"
    _hover={{
      border: "2px solid",
      borderColor: "blue.400",
      bg: "gray.800",
      transform: "scale(1.05)",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)"
    }}
    _active={{
      transform: "scale(0.98)"
    }}
    transition="all 0.2s"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    p={4}
    position="relative"
    overflow="hidden"
  >
    <Image
      src={icon}
      alt={label}
      w={iconSize}
      h={iconSize}
      objectFit="contain"
      mb={2}
    />
    <Text
      fontSize="sm"
      fontWeight="semibold"
      color="white"
      textAlign="center"
      lineHeight="1.2"
      mt={2}
    >
      {label}
    </Text>
  </Button>
);

export default LandingPage;
